import {
  Drawer,
  Stack,
  Paper,
  Typography,
  IconButton,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddEventForm from './AddEditEventForm';
import { CalendarEvent } from '@src/types/Events';
import { Stack as FracStack } from '@src/types/Stacks';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { generateValues } from './FormSections/generateValues';
import QueryKeys from '@src/constants/queryKeys';
import useEventMutations from '@src/hooks/api/mutations/useEventMutations';
import { useQueryClient } from '@tanstack/react-query';
import { eventSchema } from './schemas';
import { eventEditorStyles } from './styles';

interface EventEditorWindowProps {
  dialogOpen: boolean;
  activeEvent: CalendarEvent;
  stacksList: FracStack[];
  onClose: () => void;
  setError: (error: string | null) => void;
  setDialogOpen: (open: boolean) => void;
  setConfirmOpen: (open: boolean) => void;
}

export default function EventEditorWindow({
  dialogOpen,
  activeEvent,
  stacksList,
  onClose,
  setError,
  setDialogOpen,
  setConfirmOpen,
}: EventEditorWindowProps) {
  const theme = useTheme();
  const methods = useForm<CalendarEvent>({
    defaultValues: generateValues(activeEvent),
    resolver: yupResolver(eventSchema) as Resolver<CalendarEvent>,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { createCalendarEvent, updateCalendarEvent } = useEventMutations(
    activeEvent?.id,
  );
  const isMutating =
    createCalendarEvent.isPending || updateCalendarEvent.isPending;
  const queryClient = useQueryClient();

  const handleSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.events],
      exact: false,
    });

    setDialogOpen(false);
    setConfirmOpen(true);
  };

  const handleEventUpdates = (eventToSubmit: CalendarEvent) => {
    if (activeEvent?.id) {
      updateCalendarEvent.mutate(eventToSubmit, {
        onSuccess: handleSuccess,
      });
    } else {
      createCalendarEvent.mutate(eventToSubmit, {
        onSuccess: handleSuccess,
      });
    }
  };

  return (
    <Drawer
      anchor='right'
      open={dialogOpen}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      PaperProps={{
        sx: eventEditorStyles.drawerPaper(theme),
      }}
      variant='persistent'
    >
      <Stack sx={eventEditorStyles.contentStack} flex={1}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          component={Paper}
          sx={eventEditorStyles.header(theme)}
          elevation={0}
        >
          <Typography
            variant='h6'
            component='h2'
            sx={eventEditorStyles.titleTypography}
          >
            {activeEvent ? 'Edit Event' : 'Add Event'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <FormProvider {...methods}>
          <AddEventForm
            activeEvent={activeEvent}
            onClose={onClose}
            stacks={stacksList}
            setError={setError}
            setDialogOpen={setDialogOpen}
            setConfirmOpen={setConfirmOpen}
            handleEventUpdates={handleEventUpdates}
            isMutating={isMutating}
          />
        </FormProvider>
      </Stack>
    </Drawer>
  );
}
